/**
 * Saying what a widget counts.
 *
 * One mutation, sent a field at a time, because that is what the form is: every
 * select writes the one thing it decides. The cached list is patched with the
 * answer rather than invalidated, since the answer *is* the data source --
 * serialised whole, including the field the server resolved and the parts it
 * cleared as a consequence.
 *
 * Clearing is the reason patching matters. Choosing a different table resets the
 * view and the field server-side, and the answer says so; a client that merged
 * only what it sent would keep showing a column that belongs to another table.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { DashboardDataSource } from '@/api/types'
import { withDataSource } from './cache'

/** What one of the two configuration forms can change. */
export interface DataSourceChanges {
  table_id?: number | null
  view_id?: number | null
  field_id?: number | null
  group_by_field_id?: number | null
  aggregation_field_id?: number | null
  aggregation_type?: string
}

export interface DataSourceMutations {
  change: (dataSourceId: number, changes: DataSourceChanges) => void
  isPending: boolean
}

export function useDataSourceMutations(dashboardId: number): DataSourceMutations {
  const queryClient = useQueryClient()

  const change = useMutation({
    mutationFn: ({
      dataSourceId,
      changes,
    }: {
      dataSourceId: number
      changes: DataSourceChanges
    }): Promise<DashboardDataSource> =>
      unwrap(
        getApi().PATCH('/api/dashboard/data-sources/{data_source_id}/', {
          params: { path: { data_source_id: dataSourceId } },
          // Cast at the boundary: the body is a union over every service type
          // and these fields belong to the two a dashboard can hold.
          body: changes as never,
        }),
      ),
    onSuccess: (source) => {
      queryClient.setQueryData<DashboardDataSource[]>(
        qk.dataSources(dashboardId),
        (held) => (held === undefined ? held : withDataSource(held, source)),
      )
      // The question changed, so the answer is wrong. Invalidated rather than
      // patched, for the reason the realtime handler gives: the answer says
      // what was asked and not what it came to.
      void queryClient.invalidateQueries({
        queryKey: qk.dataSourceResult(dashboardId, source.id),
      })
    },
  })

  return {
    change: (dataSourceId, changes) => {
      change.mutate({ dataSourceId, changes })
    },
    isPending: change.isPending,
  }
}
